'use client';

import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Info,
  Clock,
  Activity,
  Zap,
  Webhook,
  Code2,
} from 'lucide-react';
import { format } from 'date-fns';

import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { cn } from '@/lib/utils';

// External Request Log type (from ClickHouse)
export interface ExternalRequestLog {
  id: string;
  request_type: 'pixel_tracking' | 'webhook_delivery' | 'api_integration';
  service: string;
  event_type: string;
  event_name: string | null;
  event_source: string | null;
  status: 'success' | 'failed' | 'info';
  retry_count: number;
  store_id: string;
  customer_id: string | null;
  order_id: string | null;
  request_url: string | null;
  response_status: number | null;
  error_message: string | null;
  requested_at: string;
  completed_at: string | null;
  created_at: string;
  duration_ms?: number;
}

interface GetExternalRequestsTableColumnsProps {
  t: (key: string) => string;
  options: {
    service: string[];
    eventType: string[];
    requestType: string[];
    status: string[];
    storeId: { id: string; name: string }[];
  };
}

// Status badge component
const StatusBadge = ({ status, t }: { status: string; t: (key: string) => string }) => {
  const statusConfig = {
    success: {
      icon: CheckCircle2,
      className: 'bg-green-100 text-green-800 border-green-200',
      label: t('status.success'),
    },
    failed: {
      icon: XCircle,
      className: 'bg-red-100 text-red-800 border-red-200',
      label: t('status.failed'),
    },
    info: {
      icon: Info,
      className: 'bg-blue-100 text-blue-800 border-blue-200',
      label: t('status.info'),
    },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.failed;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn('font-medium', config.className)}>
      <Icon className="mr-1 h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Request type badge component
const RequestTypeBadge = ({ requestType }: { requestType: string }) => {
  const typeConfig = {
    pixel_tracking: {
      icon: Zap,
      className: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'Pixel',
    },
    webhook_delivery: {
      icon: Webhook,
      className: 'bg-purple-100 text-purple-800 border-purple-200',
      label: 'Webhook',
    },
    api_integration: {
      icon: Code2,
      className: 'bg-green-100 text-green-800 border-green-200',
      label: 'API',
    },
  };

  const config = typeConfig[requestType as keyof typeof typeConfig] || {
    icon: Activity,
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    label: requestType,
  };
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn('font-medium', config.className)}>
      <Icon className="mr-1 h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Service badge component
const ServiceBadge = ({ service }: { service: string }) => {
  const serviceColors: Record<string, string> = {
    meta: 'bg-blue-100 text-blue-800 border-blue-200',
    klaviyo: 'bg-purple-100 text-purple-800 border-purple-200',
    awin: 'bg-orange-100 text-orange-800 border-orange-200',
    triplewhale: 'bg-green-100 text-green-800 border-green-200',
    converge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    webhook: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs font-medium',
        serviceColors[service.toLowerCase()] || 'border-gray-200 bg-gray-100 text-gray-800',
      )}
    >
      {service.toUpperCase()}
    </Badge>
  );
};

// Response status badge
const ResponseStatusBadge = ({ status }: { status: number | null }) => {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;

  const isSuccess = status >= 200 && status < 300;
  const isClientError = status >= 400 && status < 500;
  const isServerError = status >= 500;

  const colorClass = isSuccess
    ? 'bg-green-100 text-green-800 border-green-200'
    : isClientError
      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
      : isServerError
        ? 'bg-red-100 text-red-800 border-red-200'
        : 'bg-gray-100 text-gray-800 border-gray-200';

  return (
    <Badge variant="outline" className={cn('font-mono text-xs', colorClass)}>
      {status}
    </Badge>
  );
};

export function getExternalRequestsTableColumns({
  t,
  options,
}: GetExternalRequestsTableColumnsProps): ColumnDef<ExternalRequestLog>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('table.selectRow')}
          className="translate-y-0.5"
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => <StatusBadge status={row.original.status} t={t} />,
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('table.columns.status'),
        variant: 'select',
        options: options.status.map((status) => ({
          label: t(`status.${status}`) || status.charAt(0).toUpperCase() + status.slice(1),
          value: status,
        })),
      },
      size: 100,
    },
    {
      id: 'requestType',
      accessorKey: 'request_type',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.requestType')} />,
      cell: ({ row }) => <RequestTypeBadge requestType={row.original.request_type} />,
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('table.columns.requestTypeFull'),
        variant: 'select',
        options: options.requestType.map((type) => ({
          label: t(`requestType.${type}`) || type,
          value: type,
        })),
      },
      size: 110,
    },
    {
      id: 'service',
      accessorKey: 'service',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.service')} />,
      cell: ({ row }) => <ServiceBadge service={row.original.service} />,
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('table.columns.service'),
        variant: 'select',
        options: options.service.map((service) => ({
          label: service.charAt(0).toUpperCase() + service.slice(1),
          value: service,
        })),
      },
      size: 100,
    },
    {
      id: 'eventType',
      accessorKey: 'event_type',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.eventType')} />,
      cell: ({ row }) => {
        const eventType = row.original.event_type;
        const eventName = row.original.event_name;
        return (
          <div className="min-w-0">
            <div className="truncate font-mono text-xs font-medium">{eventType}</div>
            {eventName && <div className="text-muted-foreground truncate text-xs">→ {eventName}</div>}
          </div>
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('table.columns.eventType'),
        variant: 'select',
        options: options.eventType.map((eventType) => ({
          label: eventType
            .split('/')
            .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
            .join(' / '),
          value: eventType,
        })),
      },
      size: 180,
    },
    {
      id: 'storeId',
      accessorKey: 'store_id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.store')} />,
      cell: ({ row }) => {
        const storeId = row.original.store_id;
        return <span className="truncate font-mono text-xs">{storeId}</span>;
      },
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: t('table.columns.store'),
        variant: 'select',
        options: options.storeId.map((store) => ({
          label: store.name,
          value: store.id,
        })),
      },
      size: 180,
    },
    {
      id: 'responseStatus',
      accessorKey: 'response_status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.response')} />,
      cell: ({ row }) => <ResponseStatusBadge status={row.original.response_status} />,
      enableSorting: true,
      meta: { label: t('table.columns.response') },
      size: 90,
    },
    {
      id: 'duration',
      accessorKey: 'duration_ms',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.duration')} />,
      cell: ({ row }) => {
        const duration = row.original.duration_ms;
        if (!duration) return <span className="text-muted-foreground text-xs">—</span>;

        const displayDuration = duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(2)}s`;
        const colorClass =
          duration < 1000 ? 'text-green-700' : duration < 3000 ? 'text-yellow-700' : 'text-red-700';

        return (
          <span className={cn('font-mono text-xs', colorClass)}>
            <Clock className="mr-1 inline h-3 w-3" />
            {displayDuration}
          </span>
        );
      },
      enableSorting: true,
      meta: { label: t('table.columns.duration') },
      size: 100,
    },
    {
      id: 'requestUrl',
      accessorKey: 'request_url',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.url')} />,
      cell: ({ row }) => {
        const url = row.original.request_url;
        if (!url) return <span className="text-muted-foreground text-xs">—</span>;

        // Extract domain for display
        try {
          const urlObj = new URL(url);
          return (
            <div className="group flex items-center gap-1">
              <span className="truncate font-mono text-xs">{urlObj.hostname}</span>
              <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          );
        } catch {
          return <span className="truncate font-mono text-xs">{url}</span>;
        }
      },
      enableSorting: false,
      meta: { label: t('table.columns.url') },
      size: 180,
    },
    {
      id: 'errorMessage',
      accessorKey: 'error_message',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.error')} />,
      cell: ({ row }) => {
        const error = row.original.error_message;
        if (!error) return <span className="text-muted-foreground text-xs">—</span>;

        return (
          <div className="max-w-xs truncate text-xs text-red-600" title={error}>
            {error}
          </div>
        );
      },
      enableHiding: true,
      enableSorting: false,
      meta: { label: t('table.columns.error') },
      size: 200,
    },
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return (
          <div className="flex flex-col">
            <span className="text-xs">{format(date, 'MMM dd, HH:mm')}</span>
            <span className="text-muted-foreground text-xs">{format(date, 'yyyy')}</span>
          </div>
        );
      },
      enableSorting: true,
      enableHiding: true,
      meta: { label: t('table.columns.created') },
      size: 120,
    },
  ];
}
